<template>
  <div class="product-detail-bg">
    <div class="product-detail-box">
      <Toast
        v-if="showToast"
        :title="translations.product.toast_title"
        :message="toastMessage"
      />

      <div class="product-detail-cols">
        <!-- LEWA KOLUMNA (galeria) -->
        <div class="product-col-left">
          <div class="main-image-wrap">
            <button class="arrow left" @click="prevImage" aria-label="Poprzednie">‹</button>
            <img
              :src="mainImage || product.get_image || product.get_thumbnail"
              :alt="product.name"
              class="main-image"
              @touchstart="handleTouchStart"
              @touchend="handleTouchEnd"
            />
            <button class="arrow right" @click="nextImage" aria-label="Następne">›</button>
          </div>

          <div class="thumbs-row" v-if="thumbnails.length > 1">
            <img
              v-for="(img, idx) in thumbnails"
              :key="img + idx"
              :src="img"
              :alt="(translations.product.thumbnail_alt || 'Miniatura') + ' ' + (idx + 1)"
              class="thumb"
              :class="{ active: img === mainImage }"
              @click="changeMainImage(img)"
            />
          </div>
        </div>

        <!-- PRAWA KOLUMNA (info/zakup) -->
        <div class="product-col-right">
          <h1 class="prod-title">{{ product.name }}</h1>

          <div class="prod-price">
            <span v-if="selectedVariant">
              {{ getPriceForCurrency(selectedVariant) }} {{ getCurrencySymbol() }}
            </span>
            <span v-else class="prod-notice">
              {{ translations.product.select_variant_notice }}
            </span>
          </div>


          <!-- Inline warianty -->
          <div v-if="hasMultiLevel" style="margin-bottom:16px">
            <div v-for="(level, idx) in variantLevels" :key="idx" style="margin-bottom:14px">
              <div style="font-size:.78rem;font-weight:700;color:#888;margin-bottom:8px;text-transform:uppercase;letter-spacing:.05em">
                {{ level.name }}
              </div>
              <div style="display:flex;flex-wrap:wrap;gap:8px">
                <button v-for="opt in level.options" :key="opt"
                  type="button"
                  style="padding:10px 18px;border-radius:10px;font-size:.875rem;font-weight:600;cursor:pointer;transition:all .15s;border:2px solid"
                  :style="selectedLevelOptions[idx]===opt ? 'background:#111827;color:#fff;border-color:#111827' : 'background:#fff;color:#333;border-color:#e0e0e0'"
                  :disabled="!isOptionAvailable(idx, opt)"
                  @click="selectLevelOption(idx, opt)">
                  {{ opt }}
                </button>
              </div>
            </div>
          </div>
          <div v-if="!hasMultiLevel" style="margin-bottom:16px">
            <div style="font-size:.78rem;font-weight:700;color:#888;margin-bottom:8px;text-transform:uppercase">
              {{ translations.product.select_variant_notice || 'Wybierz wariant' }}
            </div>
            <div style="display:flex;flex-wrap:wrap;gap:8px">
              <button v-for="variant in allVariants" :key="variant.id"
                type="button"
                style="padding:10px 18px;border-radius:10px;font-size:.875rem;font-weight:600;cursor:pointer;transition:all .15s;border:2px solid"
                :style="selectedVariantId===String(variant.id) ? 'background:#111827;color:#fff;border-color:#111827' : 'background:#fff;color:#333;border-color:#e0e0e0'"
                @click="selectVariant(variant)">
                {{ getVariantName(variant) }}
              </button>
            </div>
          </div>
          <button class="big-cart-btn" :disabled="!selectedVariant" @click="addToCart" :style="!selectedVariant ? 'opacity:.4;cursor:not-allowed;filter:grayscale(1)' : ''">
            {{ translations.product.add_to_cart }}
          </button>
          <!-- TRUST BADGES -->
          <div class="trust-badges">
            <div class="trust-item">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3"/></svg>
              {{ translations.trust?.returns }}
            </div>
            <div class="trust-item">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              {{ translations.trust?.secure }}
            </div>
            <div class="trust-item">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              {{ translations.trust?.warranty }}
            </div>
          </div>

          <div v-if="product.production_time" class="prod-prodtime">
            {{ translations.product.production_time }}:
            <strong>{{ product.production_time }} {{ translations.product.days }}</strong>
          </div>
          <div v-if="selectedDeliveryTime" class="prod-deliverytime">
            {{ translations.product.delivery_time }}:
            <strong>{{ selectedDeliveryTime }} {{ translations.product.days }}</strong>
          </div>
          <div v-if="product.production_time && selectedDeliveryTime" class="prod-totaltime">
            {{ translations.product.total_delivery_time }}:
            <strong>{{ totalDeliveryTime }} {{ translations.product.days }}</strong>
          </div>

          <div v-if="availableCountries.length" class="shipping-box">
            <label for="country">{{ translations.product.select_country }}</label>
            <select
              v-model="selectedCountry"
              id="country"
              class="styled-select"
              @change="saveUserCountryISO(selectedCountry); updateShippingPrice()"
            >
              <option
                v-for="country in availableCountries"
                :key="country.code"
                :value="country.code"
              >
                {{ country.name }}
              </option>
            </select>

            <div class="shipping-price">
              {{ translations.product.shipping_cost }}
              <strong>{{ selectedShippingPrice.toFixed(2) }} {{ getCurrencySymbol() }}</strong>
            </div>

            <!-- darmowa wysylka na ten produkt -->
            <div
              v-if="selectedShippingPrice === 0"
              class="free-shipping-threshold"
            >
              🎉 200 zł darmowa wysyłka lub darmowa wysyłka na ten produkt!
            </div>


          </div>
        </div>
      </div>

      <!-- OPIS -->
      <div v-if="product.description" class="product-description" v-html="product.description" />
    </div>

    <!-- STICKY KOSZYK -->
    <div v-if="false" class="sticky-cart">
      <div class="sticky-cart-content">
        <template v-if="isMobile">
          <span class="sticky-price">
            {{ getPriceForCurrency(selectedVariant) }} {{ getCurrencySymbol() }}
          </span>
        </template>

        <template v-else>
          <span class="sticky-name">
            {{ product.name }}
            <span v-if="selectedVariant"> – {{ getVariantName(selectedVariant) }}</span>
          </span>
          <span class="sticky-price">
            {{ getPriceForCurrency(selectedVariant) }} {{ getCurrencySymbol() }}
          </span>
        </template>
      </div>
      <button class="sticky-add-btn" :disabled="!selectedVariant" @click="addToCart">
        {{ translations.product.add_to_cart }}
      </button>
    </div>

    <!-- VARIANTY – BACKDROP + SHEET -->
    <div v-if="isDrawerOpen" class="variant-backdrop" @click="closeDrawer" />
    <aside class="variant-sheet" :class="{ open: isDrawerOpen }" role="dialog" aria-modal="true">
      <header class="variant-sheet-header">
        <strong>{{ translations.product.select_variant_notice || 'Wybierz wariant' }}</strong>
        <button class="sheet-close" @click="closeDrawer" aria-label="Zamknij">×</button>
      </header>

      <div class="variant-sheet-body">
        <!-- Multi-select dla wariantów z | -->
        <div v-if="hasMultiLevel" style="padding:4px 0">
          <div v-for="(level, idx) in variantLevels" :key="idx" style="margin-bottom:14px">
            <div style="font-size:.78rem;font-weight:700;color:#888;margin-bottom:6px;text-transform:uppercase">
              {{ level.name }}
            </div>
            <div style="display:flex;flex-wrap:wrap;gap:8px">
              <button v-for="opt in level.options" :key="opt"
                type="button"
                style="padding:8px 16px;border-radius:8px;font-size:.875rem;font-weight:600;cursor:pointer;transition:all .15s;border:2px solid"
                :style="selectedLevelOptions[idx]===opt ? 'background:var(--primary,#1a1a2e);color:#fff;border-color:var(--primary,#1a1a2e)' : 'background:#fff;color:#333;border-color:#e0e0e0'"
                :disabled="!isOptionAvailable(idx, opt)"
                @click="selectLevelOption(idx, opt)">
                {{ opt }}
              </button>
            </div>
          </div>
        </div>
          <!-- Cena wybranego wariantu -->
          <div v-if="selectedVariant && hasMultiLevel" style="margin-top:16px;padding:14px;background:#f8f9fa;border-radius:12px;display:flex;justify-content:space-between;align-items:center;border:1px solid #e5e7eb">
            <span style="font-size:.875rem;color:#555;font-weight:600">{{ translations.product.select_variant_notice || 'Wybrany wariant' }}</span>
            <span style="font-size:1.3rem;font-weight:800">{{ getPriceForCurrency(selectedVariant) }} {{ getCurrencySymbol() }}</span>
          </div>
        <!-- Zwykła lista dla prostych wariantów -->
        <div v-if="!hasMultiLevel" class="sizes-column">
          <button
            v-for="variant in allVariants"
            :key="variant.id"
            type="button"
            class="size-row"
            :class="{ active: selectedVariantId === String(variant.id) }"
            @click="selectVariant(variant); closeDrawer()"
          >
            <span class="size-name">{{ getVariantName(variant) }}</span>
            <span class="size-price" v-if="getPriceForCurrency(variant)">
              {{ getPriceForCurrency(variant) }} {{ getCurrencySymbol() }}
            </span>
          </button>
        </div>
      </div>

      <footer class="variant-sheet-footer">
        <button class="big-cart-btn" :disabled="!selectedVariant" @click="addToCart(); closeDrawer()">
          {{ translations.product.add_to_cart }}
        </button>
      </footer>
    </aside>

    <!-- ===================== PODSEKCJE POD OPISEM ===================== -->

    <!-- Related products slider -->
  <AppProductReviews v-if="product && product.id" :product-id="product.id" />
    <section v-if="related.length" class="home-rail">
      <div class="rail-head">
        <h2>Polecane produkty</h2>
        <div class="rail-actions">
          <button class="rail-arrow left"  @click="scrollRail($refs.relatedRail, -1)" aria-label="Wstecz">‹</button>
          <button class="rail-arrow right" @click="scrollRail($refs.relatedRail,  1)" aria-label="Dalej">›</button>
        </div>
      </div>

      <div
        class="rail"
        ref="relatedRail"
        @mousedown="onDragStart($event, 'relatedRail')"
        @mousemove="onDragMove"
        @mouseleave="onDragEnd"
        @mouseup="onDragEnd"
        @touchstart="onDragStart($event, 'relatedRail')"
        @touchmove="onDragMove"
        @touchend="onDragEnd"
      >
        <router-link
          v-for="p in related"
          :key="p.id"
          :to="`/product/${p.slug}`"
          class="rail-item"
        >
          <div class="ri-img">
            <img :src="p.get_image" :alt="p.name" loading="lazy" />
          </div>
          <div class="ri-title">{{ p.name }}</div>
          <div class="ri-price">{{ getPriceForCurrencyFromProduct(p) }} {{ getCurrencySymbol() }}</div>
        </router-link>
      </div>
    </section>

  </div>
</template>

<script>
import AppProductReviews from '@/components/AppProductReviews.vue'
import Toast from './AppToast.vue'
import eventBus from '@/eventBus.js'
import { translations } from '@/locales/lang'
import { currentLang } from '@/locales/lang'
import { currentCurrency } from '@/composables/currency'

const API = ''

export default {
  components: { AppProductReviews, Toast },
  name: 'AppProductDetail',
  data() {
    return {
      product: {},
      loading: true,
      error: null,
      thumbnails: [],
      mainImage: null,

      variantGroups: {},
      selectedVariant: null,
      selectedVariantId: '',

      selectedCountry: '',
      availableCountries: [],
      selectedShippingPrice: 0,
      selectedDeliveryTime: 0,

      touchStartX: 0,
      showToast: false,
      toastMessage: '',
      isMobile: false,
      showStickyCart: false,
      isDrawerOpen: false,
      selectedLevelOptions: [],

      related: [],
      categories: [],

      dragging: false,
      dragStartX: 0,
      scrollStartLeft: 0,
      dragRef: null,

      // Podsumowanie koszyka (w aktualnej walucie)
      cartSubtotal: 0,

      // Progi darmowej wysyłki (przykładowo)
      freeShippingThresholds: { PLN: 200, EUR: 50, USD: 55, CZK: 1200, HUF: 20000, BGN: 100, RON: 250, DKK: 370, SEK: 550 },
      defaultFreeShippingThreshold: Infinity,

      // PIXEL
      lastTrackedVariantId: null
    }
  },
  computed: {
    translations() { return translations.value },
    currentCurrency() { return currentCurrency.value },
    activeLang() { return currentLang.value },
    totalDeliveryTime() {
      return (Number(this.product.production_time || 0) + Number(this.selectedDeliveryTime || 0))
    },
    allVariants() {
      return Object.values(this.variantGroups || {}).flat()
    },
    currentFreeShippingThreshold() {
      return this.freeShippingThresholds[this.currentCurrency] ?? this.defaultFreeShippingThreshold
    },
    freeShippingActive() {
      return this.cartSubtotal >= this.currentFreeShippingThreshold
    },
    remainingForFreeShipping() {
      const left = this.currentFreeShippingThreshold - this.cartSubtotal
      return left > 0 ? left : 0
    },
    hasMultiLevel() {
      if (!this.product || !this.product.variants) return false
      return this.product.variants.some(v => (v.type && v.type.name && v.type.name.includes('|')) || (v.value || '').includes('|'))
    },
    variantLevels() {
      if (!this.hasMultiLevel) return []
      const variants = this.product.variants || []
      const multiVariants = variants.filter(v => (v.type && v.type.name && v.type.name.includes('|')) || (v.value || '').includes('|'))
      if (!multiVariants.length) return []
      const getBase = (v) => (v.value_pl || v.value || '')
      const levelCount = Math.max(...multiVariants.map(v => getBase(v).split('|').length))
      const typeName = multiVariants[0]?.type?.name || ''
      const typeNames = typeName.split('|').map(n => n.trim())
      const levels = []
      for (let i = 0; i < levelCount; i++) {
        const opts = [...new Set(multiVariants.map(v => getBase(v).split('|')[i]).filter(Boolean))]
        levels.push({ name: typeNames[i] || ('Poziom ' + (i+1)), options: opts })
      }
      return levels
    },
  },
  watch: {
    currentCurrency() {
      if (this.selectedVariant) this.selectVariant(this.selectedVariant)
      this.refreshCartSubtotal()
      this._pixelViewContentSafe()
    },
    selectedVariantId(newId) {
      const v = this.allVariants.find(x => String(x.id) === String(newId))
      if (v) this.selectVariant(v)
    },
    async activeLang() {
      const prevVariantId = this.selectedVariant && this.selectedVariant.id
      await this.fetchProduct()
      await Promise.all([ this.loadCategories(), this.loadRelated() ])
      if (prevVariantId) {
        const same = this.allVariants.find(v => String(v.id) === String(prevVariantId))
        if (same) this.selectVariant(same)
      }
      this._pixelViewContentSafe()
    },
    '$route.params.slug': {
      immediate: false,
      handler() { this.handleRouteChange() }
    }
  },
  beforeRouteUpdate(to, from, next) {
    if (to.params.slug !== from.params.slug) {
      this.handleRouteChange().finally(() => next())
    } else {
      next()
    }
  },
  mounted() {
    this.isMobile = window.innerWidth <= 768
    window.addEventListener('scroll', () => {
      this.showStickyCart = this.isMobile ? window.scrollY > 300 : window.scrollY > 600
    })

    try { eventBus?.on?.('cartUpdated', this.refreshCartSubtotal) } catch (e) { console.debug(e) }
    window.addEventListener('currencyChanged', this.refreshCartSubtotal)

    this.prefetchIPCountry().finally(async () => {
      await this.fetchProduct()
      await Promise.all([ this.loadCategories(), this.loadRelated() ])
      this.refreshCartSubtotal()
      this._pixelViewContentSafe()
    })
  },
  beforeUnmount() {
    try { eventBus?.off?.('cartUpdated', this.refreshCartSubtotal) } catch (e) { console.debug(e) }
    window.removeEventListener('currencyChanged', this.refreshCartSubtotal)
  },
  methods: {
    /* ===== PIXEL helpers ===== */
    _fbqAvailable() { return typeof window !== 'undefined' && typeof window.fbq === 'function' },
    _fbqTrack(eventName, payload = {}) {
      try {
        if (this._fbqAvailable()) window.fbq('track', eventName, payload)
      } catch (e) { void e }
    },
    _getNumericPrice(variant) {
      if (!variant) return 0
      const found = (variant.prices || []).find(p => p.currency && p.currency.code === this.currentCurrency)
      const num = Number(found && found.amount)
      return Number.isFinite(num) ? num : 0
    },
    _pixelViewContentSafe() {
      if (!this.product || !this.product.id) return
      const variant = this.selectedVariant || (this.allVariants[0] || null)
      const vId = (variant && variant.id) ? variant.id : 'no-variant'
      if (this.lastTrackedVariantId === vId) return
      this.lastTrackedVariantId = vId

      const price = this._getNumericPrice(variant)
      this._fbqTrack('ViewContent', {
        content_ids: [ String(this.product.id) ],
        content_type: 'product',
        value: price,
        currency: this.currentCurrency,
        contents: [{ id: String(this.product.id), quantity: 1, item_price: price }]
      })
    },
    _pixelAddToCart(quantity = 1) {
      if (!this.product || !this.product.id || !this.selectedVariant) return
      const price = this._getNumericPrice(this.selectedVariant)
      this._fbqTrack('AddToCart', {
        content_ids: [ String(this.product.id) ],
        content_type: 'product',
        value: price * quantity,
        currency: this.currentCurrency,
        contents: [{ id: String(this.product.id), quantity, item_price: price }]
      })
    },

    /* reset i routing */
    resetState() {
      this.product = {}
      this.loading = true
      this.error = null
      this.thumbnails = []
      this.mainImage = null
      this.variantGroups = {}
      this.selectedVariant = null
      this.selectedVariantId = ''
      this.selectedCountry = ''
      this.availableCountries = []
      this.selectedShippingPrice = 0
      this.selectedDeliveryTime = 0
      this.showToast = false
      this.toastMessage = ''
      this.isDrawerOpen = false
      this.related = []
      this.lastTrackedVariantId = null
    },
    async handleRouteChange() {
      try {
        this.resetState()
        await this.fetchProduct()
        await Promise.all([ this.loadCategories(), this.loadRelated() ])
        window.scrollTo({ top: 0, behavior: 'smooth' })
        this._pixelViewContentSafe()
      } catch (e) {
        console.warn('handleRouteChange failed', e)
      } finally {
        this.loading = false
      }
    },

    /* IP → ISO */
    async prefetchIPCountry() {
      try {
        const qs = new URLSearchParams(window.location.search).get('ship_to')
        if (qs) return
        if (localStorage.getItem('ship_to_iso')) return

        const ctrl = new AbortController()
        const t = setTimeout(() => ctrl.abort(), 1500)
        const res = await fetch('https://ipapi.co/json/', { signal: ctrl.signal })
        clearTimeout(t)
        if (!res.ok) return
        const data = await res.json()
        const iso = (data && data.country_code) ? String(data.country_code).toUpperCase() : ''
        if (iso) localStorage.setItem('ship_to_iso', iso)
      } catch (e) {
        console.debug('prefetchIPCountry skipped:', e)
      }
    },

    /* produkt */
    async fetchProduct() {
      const slug = this.$route.params.slug
      try {
        const res = await fetch(`${API}/api/v1/products/${slug}/`, {
          headers: { 'Accept-Language': currentLang.value }
        })
        if (!res.ok) throw new Error('Błąd API')
        const data = await res.json()

        this.product = data
        this.thumbnails = [
          data.get_image,
          ...(Array.isArray(data.images) ? data.images.map(img => img.image_url) : [])
        ].filter(Boolean)
        this.mainImage = this.thumbnails[0] || data.get_image

        const grouped = {}
        ;(data.variants || []).forEach(v => {
          const group = (v.type && v.type.name) || 'Wariant'
          if (!grouped[group]) grouped[group] = []
          grouped[group].push(v)
        })
        if (Object.keys(grouped).length === 0 && (data.variants || []).length) {
          grouped['Wariant'] = data.variants
        }
        this.variantGroups = grouped

        if (this.allVariants.length && !this.selectedVariant) {
          if (!this.hasMultiLevel) this.selectVariant(this.allVariants[0])
        }

        this.loading = false
      } catch (err) {
        this.error = err.message
        this.loading = false
      }
    },

    changeMainImage(img) { this.mainImage = img },

    /* kraje */
    getUserCountryISO() {
      const qs = new URLSearchParams(window.location.search).get('ship_to')
      if (qs) return qs.toUpperCase()
      const stored = localStorage.getItem('ship_to_iso')
      if (stored) return stored.toUpperCase()
      const m = (navigator.language || '').match(/-([A-Za-z]{2})$/)
      return m ? m[1].toUpperCase() : ''
    },
    regionToLang(code) {
      const m = {
        PL:'pl', DE:'de', AT:'de', BE:'nl', BG:'bg', HR:'hr', CZ:'cs', DK:'da', EE:'et',
        ES:'es', FR:'fr', NL:'nl', LT:'lt', LV:'lv', RO:'ro', SK:'sk', SI:'sl',
        SE:'sv', IT:'it', HU:'hu', PT:'pt', IE:'en'
      }
      return m[String(code).toUpperCase()] || 'en'
    },
    countryEndonym(iso) {
      const lang = this.regionToLang(iso)
      try {
        const dn = new Intl.DisplayNames([lang], { type: 'region' })
        return dn.of(String(iso).toUpperCase()) || String(iso).toUpperCase()
      } catch { return String(iso).toUpperCase() }
    },
    countryLabel(iso) {
      const code = String(iso).toUpperCase()
      return this.countryEndonym(code)
    },
    saveUserCountryISO(iso) {
      if (iso) localStorage.setItem('ship_to_iso', String(iso).toUpperCase())
    },

    /* wariant + wysyłka */
    selectVariant(variant) {
      this.selectedVariant = variant
      this.selectedVariantId = String(variant && variant.id ? variant.id : '')

      this.availableCountries = (variant.shipping_options || []).map(opt => {
        const code = String(opt.country || '').toUpperCase()
        const label = /^[A-Z]{2}$/.test(code) ? this.countryLabel(code) : (opt.country_name || code)
        return {
          code,
          name: label,
          PLN: Number(opt.price_pln ?? 0),
          EUR: Number(opt.price_eur ?? 0),
          USD: Number(opt.price_usd ?? 0),
          CZK: Number(opt.price_czk ?? 0),
          HUF: Number(opt.price_huf ?? 0),
          BGN: Number(opt.price_bgn ?? 0),
          RON: Number(opt.price_ron ?? 0),
          DKK: Number(opt.price_dkk ?? 0),
          SEK: Number(opt.price_sek ?? 0),
          delivery_time: Number(opt.delivery_time ?? 0)
        }
      })

      const preferred = (this.getUserCountryISO() || '').toUpperCase()
      const codes = this.availableCountries.map(c => c.code)
      this.selectedCountry = codes.includes(preferred) ? preferred : (codes[0] || '')
      this.updateShippingPrice()

      this._pixelViewContentSafe()
    },

    updateShippingPrice() {
      const sel = (this.selectedCountry || '').toUpperCase()
      const option = this.availableCountries.find(o => String(o.code).toUpperCase() === sel)
      if (!option) {
        this.selectedShippingPrice = 0
        this.selectedDeliveryTime = 0
        return
      }
      this.selectedDeliveryTime = option.delivery_time || 0
      const val = Number(option[this.currentCurrency] ?? 0)
      this.selectedShippingPrice = Number.isFinite(val) ? val : 0
    },

    /* ceny */
    getPriceForCurrency(variant) {
      if (!variant) return ''
      const found = (variant.prices || []).find(p => p.currency && p.currency.code === this.currentCurrency)
      if (!found || found.amount == null) return ''
      return Number(found.amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
    },
    getPriceForCurrencyFromProduct(product) {
      const map = {
        PLN:'price_pln', EUR:'price_eur', USD:'price_usd',
        CZK:'price_czk', HUF:'price_huf', BGN:'price_bgn',
        RON:'price_ron', DKK:'price_dkk', SEK:'price_sek'
      }
      const key = map[this.currentCurrency] || 'price_pln'
      const num = Number(product && product[key])
      return Number.isFinite(num) ? num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g,' ') : '0.00'
    },
    selectLevelOption(idx, opt) {
      const newSelected = [...this.selectedLevelOptions]
      newSelected[idx] = opt
      // Reset poziomow nizszych
      for (let i = idx+1; i < this.variantLevels.length; i++) {
        newSelected[i] = null
      }
      this.selectedLevelOptions = newSelected
      // Znajdz pasujacy wariant
      const value = newSelected.filter(Boolean).join('|')
      if (newSelected.filter(Boolean).length === this.variantLevels.length) {
        const allV = this.product.variants || []
      const found = allV.find(v => (v.value_pl || v.value || '') === value)
        if (found) {
          this.selectVariant(found)
        } else {
          this.selectedVariant = null
          this.selectedVariantId = ''
        }
      }
    },
    isOptionAvailable(levelIdx, opt) {
      // Sprawdz czy opcja jest dostepna przy aktualnych wyborach
      const prefix = this.selectedLevelOptions.slice(0, levelIdx).filter(Boolean)
      const allV = this.product.variants || []
      return allV.some(v => {
        const parts = (v.value_pl || v.value || '').split('|')
        return prefix.every((p, i) => parts[i] === p) && parts[levelIdx] === opt
      })
    },
    getVariantName(variant) {
      const lang = this.currentLanguage || 'pl'
      const translated = variant['value_'+lang]
      if (translated && !translated.includes('|')) return translated
      const val = variant.value || ''
      if (val.includes('|')) {
        // Pokaz jako "Cyfra: 5 / Wersja: Z LED"
        const parts = val.split('|')
        const typeName = (variant.type && variant.type.name) || ''
        const typeNames = typeName.split('|').map(n => n.trim())
        return parts.map((p, i) => (typeNames[i] ? typeNames[i] + ': ' + p.trim() : p.trim())).join(' / ')
      }
      return val || (variant.type && variant.type.name) || ''
    },
    getCurrencySymbol() {
      const symbols = { PLN:'zł', USD:'$', EUR:'€', CZK:'Kč', HUF:'Ft', BGN:'лв', RON:'lei', DKK:'kr.', SEK:'kr' }
      return symbols[this.currentCurrency] || this.currentCurrency
    },

    /* koszyk */
    addToCart() {
      if (!this.selectedVariant) {
        return alert(this.translations.product.no_variant || 'Nie wybrano wariantu!')
      }
      const variant = this.selectedVariant
      const price = this.getPriceForCurrency(variant)

      let cart = []
      try { cart = JSON.parse(localStorage.getItem('cart') || '[]') } catch { cart = [] }

      const idx = cart.findIndex(
        item =>
          item.slug === this.product.slug &&
          item.id === this.product.id &&
          item.variantKey === String(variant.id)
      )

      if (idx > -1) cart[idx].quantity++
      else {
        cart.push({
          id: this.product.id,
          slug: this.product.slug,
          name: this.product.name,
          price: Number(String(price).replace(/\s/g, '').replace(',', '.')) || 0,
          currency: this.currentCurrency,
          quantity: 1,
          variantKey: String(variant.id),
          variantId: Number(variant.id),
          variants: [{ id: variant.id, value: variant.type && variant.type.name, type: variant.type && variant.type.name }]
        })
      }

      localStorage.setItem('cart', JSON.stringify(cart))
      this.toastMessage = `${this.product.name} ${(this.translations.product.added_to_cart_suffix || 'został dodany pomyślnie.')}`
      this.showToast = true
      setTimeout(() => (this.showToast = false), 3000)
      try { eventBus.emit('cartUpdated') } catch (e) { console.debug('emit cartUpdated failed:', e) }
      this.refreshCartSubtotal()

      // Pixel: AddToCart
      this._pixelAddToCart(1)
    },

    /* galeria */
    nextImage() {
      const index = this.thumbnails.indexOf(this.mainImage)
      const nextIndex = (index + 1) % this.thumbnails.length
      this.mainImage = this.thumbnails[nextIndex]
    },
    prevImage() {
      const index = this.thumbnails.indexOf(this.mainImage)
      const prevIndex = (index - 1 + this.thumbnails.length) % this.thumbnails.length
      this.mainImage = this.thumbnails[prevIndex]
    },
    handleTouchStart(e) { this.touchStartX = e.changedTouches[0].screenX },
    handleTouchEnd(e) {
      const touchEndX = e.changedTouches[0].screenX
      const diff = this.touchStartX - touchEndX
      if (diff > 50) this.nextImage()
      else if (diff < -50) this.prevImage()
    },

    closeDrawer() { this.isDrawerOpen = false },

    /* related + categories */
    async loadRelated() {
      try {
        const catSlug = (this.product && this.product.category && this.product.category.slug) ||
                        (this.product && Array.isArray(this.product.categories) && this.product.categories[0] && this.product.categories[0].slug)
        let list = []
        if (catSlug) {
          const r = await fetch(`${API}/api/v1/categories/${catSlug}/`, {
            headers: { 'Accept-Language': currentLang.value }
          })
          const cat = await r.json()
          list = Array.isArray(cat.products) ? cat.products : []
        }
        if (!list.length) {
          const r = await fetch(`${API}/api/v1/latest-products/`, {
            headers: { 'Accept-Language': currentLang.value }
          })
          const data = await r.json()
          list = Array.isArray(data) ? data : []
        }
        this.related = list.filter(p => p.id !== this.product.id).slice(0, 12)
      } catch (e) {
        console.warn('loadRelated failed', e)
        this.related = []
      }
    },

    async loadCategories() {
      try {
        const res = await fetch(`${API}/api/v1/categories/`, {
          headers: { 'Accept-Language': currentLang.value }
        })
        const data = await res.json()
        this.categories = Array.isArray(data) ? data : (data.results || [])
      } catch (e) {
        console.warn('loadCategories failed', e)
        this.categories = []
      }
    },

    getCategoryImage(cat){
      return cat.get_image || cat.get_thumbnail || '/placeholder.png'
    },

    /* rail */
    scrollRail(el, dir) {
      if (!el) return
      const card = el.querySelector('.rail-item')
      const step = card ? card.offsetWidth + 16 : 300
      el.scrollBy({ left: dir * step * 2, behavior: 'smooth' })
    },
    onDragStart(e, refName) {
      const el = this.$refs[refName]
      if (!el) return
      this.dragging = true
      this.dragRef = el
      this.dragStartX = (e.touches ? e.touches[0].clientX : e.clientX)
      this.scrollStartLeft = el.scrollLeft
    },
    onDragMove(e) {
      if (!this.dragging || !this.dragRef) return
      const x = (e.touches ? e.touches[0].clientX : e.clientX)
      const delta = this.dragStartX - x
      this.dragRef.scrollLeft = this.scrollStartLeft + delta
    },
    onDragEnd() { this.dragging = false; this.dragRef = null },

    /* koszyk subtotal */
    async refreshCartSubtotal() {
      try {
        let rawCart = []
        try {
          rawCart = JSON.parse(localStorage.getItem('cart') || '[]')
        } catch { rawCart = [] }
        rawCart = rawCart.filter(e => e && e.slug && e.id)

        const currencyCode = this.currentCurrency || 'PLN'
        const parts = await Promise.all(
          rawCart.map(async (entry) => {
            try {
              if (entry.currency === currencyCode && typeof entry.price === 'number') {
                return Number(entry.price) * (Number(entry.quantity) || 1)
              }
              const res = await fetch(`/api/v1/products/${entry.slug}/`, {
                headers: { 'Accept-Language': currentLang.value }
              })
              if (!res.ok) throw new Error('API error')
              const product = await res.json()
              let price = parseFloat(product.price) || 0
              const vid = entry.variantId || (entry.variantKey ? Number(entry.variantKey) : null)
              if (vid && product.variants) {
                const variant = product.variants.find(v => Number(v.id) === Number(vid))
                if (variant) {
                  const found = variant.prices && variant.prices.find(p => p.currency && p.currency.code === currencyCode)
                  if (found) price = parseFloat(found.amount) || 0
                }
              }
              return price * (Number(entry.quantity) || 1)
            } catch (e) {
              console.debug('refreshCartSubtotal item failed:', e)
              return 0
            }
          })
        )
        this.cartSubtotal = parts.reduce((a, b) => a + (Number(b) || 0), 0)
      } catch (e) {
        console.debug('refreshCartSubtotal failed:', e)
        this.cartSubtotal = 0
      }
    }
  }
}
</script>

<style scoped>
/* NAGŁÓWEK NAD RAMKĄ */
.picker-heading{ font-weight:700; font-size:.95rem; margin:.4rem 0 .5rem; opacity:.85; }

/* RAMKA: lewo TYPE.NAME, prawo CENA + caret */
.variant-picker{
  display:flex; align-items:center; gap:1rem;
  width:100%; padding:.9rem 1rem;
  border:1px solid #e5e7eb; border-radius:12px;
  background:#fff; cursor:pointer; margin:.25rem 0 1rem;
}
.picker-value{ font-weight:600; }
.picker-right{ margin-left:auto; display:flex; align-items:center; gap:.5rem; }
.picker-price{ font-weight:800; }
.picker-caret{ width:18px; height:18px; color:#6b7280; }

/* BACKDROP */
.variant-backdrop{ position:fixed; inset:0; background:rgba(0,0,0,.38); backdrop-filter:blur(2px); z-index:1100; }

/* BOTTOM SHEET */
.variant-sheet{
  position:fixed; left:50%; bottom:0; transform:translate(-50%, 100%);
  width:100%; max-width:560px; height:min(85vh, 720px);
  background:#fff; border-top-left-radius:16px; border-top-right-radius:16px;
  box-shadow:0 -16px 32px rgba(0,0,0,.18);
  transition:transform .28s ease; z-index:1110;
  display:grid; grid-template-rows:auto 1fr auto; border:1px solid #eef1f6; border-bottom:none;
}
.variant-sheet.open{ transform:translate(-50%, 0); }
.variant-sheet-header{
  display:flex; align-items:center; justify-content:space-between;
  padding:1rem 1.25rem; border-bottom:1px solid #f1f5f9;
}
.sheet-close{ border:none; background:transparent; cursor:pointer; font-size:1.25rem; line-height:1; }
.variant-sheet-body{ overflow:auto; padding:1rem 1.25rem 0; }
.variant-sheet-footer{ padding:1rem 1.25rem 1.25rem; border-top:1px solid #f1f5f9; }

/* Lista wariantów */
.sizes-column{ display:flex; flex-direction:column; gap:.5rem; }
.size-row{
  display:flex; justify-content:space-between; align-items:center; width:100%;
  padding:.75rem .9rem; border-radius:12px; background:#fff;
  border:1px solid #e5e7eb; cursor:pointer;
}
.size-row:hover{ border-color:#c7cad1; }
.size-row.active{ border-color:#111827; box-shadow:0 0 0 2px #111827 inset; }
.size-name{ font-weight:600; }
.size-price{ opacity:.95; font-weight:800; }

/* NOWE: informacja o progu oraz pasek */
.free-shipping-threshold{
  margin-top:.3rem;
  font-size:.92rem;
  color:#475569;
}
.free-shipping-inline{
  margin-top:.5rem;
  padding:.5rem .75rem;
  background:#f0f8ff;
  border:1px solid #cde;
  border-radius:8px;
  font-size:.95rem;
}
</style>

<!-- GLOBAL – rail/slider -->
<style>
.home-rail{ max-width:1400px; margin:28px auto; padding:0 12px; }
.rail-head{ display:flex; align-items:center; justify-content:space-between; padding:6px 4px 10px; }
.rail-head h2{ font-weight:800; font-size:clamp(1.2rem,2.2vw,1.6rem); margin:0; }
.rail-actions{ display:flex; gap:8px; }
.rail-arrow{
  width:38px; height:38px; border-radius:50%;
  background:#fff; border:1px solid #e8ecf5; cursor:pointer;
  box-shadow:0 2px 10px rgba(0,0,0,.06); font-size:20px; display:grid; place-items:center;
}
.rail-arrow:hover{ background:#f7f8fb; }

.rail{
  display:grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(220px, 280px);
  gap:16px;
  overflow-x:auto;
  padding: 6px 2px 4px;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
}
.rail::-webkit-scrollbar{ height: 0; }

.rail-item{
  scroll-snap-align: start;
  display:flex; flex-direction:column;
  background:#fff;
  border:1px solid #e9edf6;
  border-radius:14px;
  text-decoration:none; color:inherit;
  overflow:hidden;
  transition:transform .15s ease, box-shadow .15s ease, border-color .15s ease;
}
.rail-item:hover{
  transform: translateY(-2px);
  border-color:#dbe3f4;
  box-shadow: 0 8px 18px rgba(20,30,60,.05);
}
.ri-img{ width:100%; aspect-ratio: 4/3; display:flex; align-items:center; justify-content:center; background:#fff; overflow:hidden; }
.ri-img img{ width:100%; height:100%; object-fit:cover; }
.ri-title{ padding:10px 12px 4px; font-weight:700; font-size:.98rem; line-height:1.3; }
.ri-price{ padding:0 12px 12px; font-weight:800; font-size:1rem; }
</style>
